import {
  userCountryGeolocated,
  userGeolocationFailed,
} from '@actions/app.action';
import {
  availabilitySearchAllHome,
  availabilitySearchAll,
  availabilitySellTripSelectionSuccess,
  availabilitySearchByQueryParams,
  availabilitySearchFromDestinations,
} from '@actions/availability.action';
import {
  bookingStoreBooking,
  bookingGetBookingSuccess,
  bookingRetrieveBookingSuccess,
  bookingCancelFlightsSuccess,
} from '@actions/booking.action';
import {
  updateSelectedCurrencyCode,
  updateGeolocationFallback,
} from '@actions/culture.action';
import { setHeaders } from '@actions/headers.action';
import {
  COUNTRY_CODE_TO_CURRENCY,
  GEOLOCATION_FALLBACK_CURRENCY,
  QuasiContinent,
  QUASI_CONTINENT_US_COUNTRIES,
} from '@constants/app.constants';
import { Action, createReducer, on } from '@ngrx/store';

export interface State {
  geolocatedCountryCode: string | null;
  selectedCurrencyCode: string | null;
  userQuasiContinent: QuasiContinent | null;
}

export const INITIAL_STATE: State = {
  geolocatedCountryCode: null,
  selectedCurrencyCode: null,
  userQuasiContinent: null,
};

const reducer = createReducer(
  INITIAL_STATE,
  on(
    userCountryGeolocated,
    (state, { countryCode }): State => ({
      ...state,
      geolocatedCountryCode: countryCode,
      selectedCurrencyCode:
        state.selectedCurrencyCode ??
        (countryCode
          ? COUNTRY_CODE_TO_CURRENCY[countryCode] ??
            GEOLOCATION_FALLBACK_CURRENCY
          : null),
      userQuasiContinent:
        countryCode && QUASI_CONTINENT_US_COUNTRIES.includes(countryCode)
          ? QuasiContinent.America
          : QuasiContinent.NotAmerica,
    }),
  ),

  on(
    userGeolocationFailed,
    (state): State => ({
      ...state,
      geolocatedCountryCode: null,
      userQuasiContinent: null,
    }),
  ),

  on(
    updateSelectedCurrencyCode,
    (state, action): State => ({
      ...state,
      selectedCurrencyCode: action.code,
    }),
  ),

  on(
    updateGeolocationFallback,
    (state): State => ({
      ...state,
      selectedCurrencyCode: GEOLOCATION_FALLBACK_CURRENCY,
    }),
  ),

  // on search success we update the currency to reflect what was last searched
  // on booking load we force the currency dropdown to display the currency of their booking
  on(
    availabilitySearchAllHome,
    availabilitySearchAll,
    availabilitySearchByQueryParams,
    availabilitySearchFromDestinations,
    bookingStoreBooking,
    availabilitySellTripSelectionSuccess,
    (state, action): State => ({
      ...state,
      selectedCurrencyCode:
        action.payload.currencyCode ?? state.selectedCurrencyCode,
    }),
  ),

  // on booking load we force the currency dropdown to display the currency of their booking
  on(
    bookingGetBookingSuccess,
    bookingRetrieveBookingSuccess,
    bookingCancelFlightsSuccess,
    (state, action): State => ({
      ...state,
      selectedCurrencyCode:
        action.booking.currencyCode ?? state.selectedCurrencyCode,
    }),
  ),
  on(
    setHeaders,
    (state, { geoClientCountry, is201Enabled }): State =>
      is201Enabled
        ? {
            ...state,
            geolocatedCountryCode: geoClientCountry,
            selectedCurrencyCode:
              state.selectedCurrencyCode ??
              (geoClientCountry
                ? COUNTRY_CODE_TO_CURRENCY[geoClientCountry] ??
                  GEOLOCATION_FALLBACK_CURRENCY
                : null),
            userQuasiContinent:
              geoClientCountry &&
              QUASI_CONTINENT_US_COUNTRIES.includes(geoClientCountry)
                ? QuasiContinent.America
                : QuasiContinent.NotAmerica,
          }
        : {
            ...state,
          },
  ),
);

export const cultureReducer = (state: State, action: Action): State =>
  reducer(state, action);
